import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import { KEY_BOARD, FOCUS_TEMPLATE_FEATURE } from 'constants/general';
import COLOR from 'constants/color';
import { Add, TrashOutlined, Globe, Apps } from 'assets/icons/revamp';
import Card from 'components/ui/Card';
import { t } from 'services/i18n.service';
import BaseButton from 'components/ui/BaseButton';
import { toast } from 'react-toastify';
import { DEFAULT_HTML_ATTRIBUTES } from 'assets/data';
import { updateCurrentViewedFocusTemplate } from 'store/reducer/focus-mode/slice';
import {
  isValidAllowedWebsiteUrl,
  normalizeAllowedWebsiteUrl
} from 'utils/validators';

interface AllowedItemPillProps {
  label: string;
  onRemove: () => void;
  removeAriaLabel: string;
}

const AllowedItemPill = ({
  label,
  onRemove,
  removeAriaLabel
}: AllowedItemPillProps) => (
  <div className='flex items-center ~gap-1.5/2 ~px-2/3 ~py-1/1.5 bg-gray-100 dark:bg-primary-dark-50 rounded-full border border-primary-dark-200 dark:border-primary-900'>
    <span className='~text-xs/sm text-black dark:text-white break-all'>
      {label}
    </span>
    <button
      type='button'
      onClick={onRemove}
      className='flex-shrink-0 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full ~p-0.5/1 transition-colors'
      aria-label={removeAriaLabel}>
      <TrashOutlined className='~w-3/5 ~h-3/5' fill={COLOR.SEMANTIC.ERROR} />
    </button>
  </div>
);

const useUpdateAllowedList = (
  feature:
    | typeof FOCUS_TEMPLATE_FEATURE.ALLOWED_URLS
    | typeof FOCUS_TEMPLATE_FEATURE.ALLOWED_APPS
) => {
  const dispatch = useAppDispatch();

  return useCallback(
    (items: string[]) => {
      dispatch(
        updateCurrentViewedFocusTemplate({
          focusTemplateFeature: feature,
          value: items
        })
      );
    },
    [dispatch, feature]
  );
};

const AllowedUrlsCard = () => {
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const allowedUrls = useAppSelectorShallow(
    (state) => state.focusMode.currentViewedFocusTemplate?.allowed_urls ?? []
  );
  const updateAllowedUrls = useUpdateAllowedList(
    FOCUS_TEMPLATE_FEATURE.ALLOWED_URLS
  );

  const handleUrlInputChange = (value: string) => {
    setUrlInput(value);
    if (urlError.length > 0) {
      setUrlError('');
    }
  };

  const handleAddUrl = () => {
    const url = normalizeAllowedWebsiteUrl(urlInput);
    if (!url.length) {
      return;
    }
    if (!isValidAllowedWebsiteUrl(urlInput)) {
      const message = t('validation.invalid_url');
      setUrlError(message);
      toast.error(message);
      return;
    }
    const isDuplicate = allowedUrls.some(
      (existingUrl) => existingUrl.toLowerCase() === url.toLowerCase()
    );
    if (isDuplicate) {
      toast.error(t('tabs_revamp.allowed_apps_tab.website_url_already_added'));
      return;
    }
    updateAllowedUrls([...allowedUrls, url]);
    setUrlInput('');
    setUrlError('');
  };

  const handleDeleteUrl = (url: string) => {
    updateAllowedUrls(allowedUrls.filter((item) => item !== url));
  };

  return (
    <Card className='flex flex-col ~gap-3/4' data-testid='allowed-items-urls'>
      <div className='flex flex-col ~gap-1/2'>
        <h3 className='~text-xs/base font-semibold flex items-center ~gap-1/2'>
          <Globe styles='~w-4/5 ~h-4/5' fill={COLOR.PRIMARY.LIGHT_900} />
          {t('title_allowed_url')}
        </h3>
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
          {t('tabs_revamp.allowed_apps_tab.allow_websites_description')}
        </p>
      </div>
      {allowedUrls.length > 0 && (
        <div className='flex flex-wrap items-center ~gap-2/3'>
          {allowedUrls.map((url) => (
            <AllowedItemPill
              key={url}
              label={url}
              onRemove={() => handleDeleteUrl(url)}
              removeAriaLabel={t(
                'tabs_revamp.allowed_apps_tab.remove_website',
                { url }
              )}
            />
          ))}
        </div>
      )}
      <div className='flex flex-col ~gap-2/3'>
        <FloatingLabelInput
          type='text'
          placeholder={t('tabs_revamp.allowed_apps_tab.enter_url')}
          value={urlInput}
          onChange={(event) => handleUrlInputChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === KEY_BOARD.ENTER.KEY) {
              handleAddUrl();
            }
          }}
          size='small'
          showLabel={true}
          label=''
          error={urlError}
          {...DEFAULT_HTML_ATTRIBUTES}
        />
        <div className='flex justify-end'>
          <BaseButton
            title={t('tabs_revamp.allowed_apps_tab.add_url')}
            onClick={handleAddUrl}
            variant='secondary'
            icon={<Add styles='~w-3/4 ~h-3/4' fill={COLOR.PRIMARY.LIGHT_900} />}
            size='sm'
          />
        </div>
      </div>
    </Card>
  );
};

const AllowedAppsCard = () => {
  const [appInput, setAppInput] = useState('');
  const allowedApps = useAppSelectorShallow(
    (state) => state.focusMode.currentViewedFocusTemplate?.allowed_apps ?? []
  );
  const updateAllowedApps = useUpdateAllowedList(
    FOCUS_TEMPLATE_FEATURE.ALLOWED_APPS
  );

  const handleAddApp = () => {
    const appName = appInput.trim();
    if (appName.length === 0) {
      return;
    }
    const isDuplicate = allowedApps.some(
      (app) => app.toLowerCase() === appName.toLowerCase()
    );
    if (isDuplicate) {
      toast.error(t('market.already_added'));
      return;
    }
    updateAllowedApps([...allowedApps, appName]);
    setAppInput('');
  };

  const handleDeleteApp = (appName: string) => {
    updateAllowedApps(allowedApps.filter((item) => item !== appName));
  };

  return (
    <Card className='flex flex-col ~gap-3/4' data-testid='allowed-items-apps'>
      <div className='flex flex-col ~gap-1/2'>
        <h3 className='~text-xs/base font-semibold flex items-center ~gap-1/2'>
          <Apps styles='~w-4/5 ~h-4/5' fill={COLOR.PRIMARY.LIGHT_900} />
          {t('focus_mode.allowed_apps')}
        </h3>
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
          {t('tabs_revamp.allowed_apps_tab.allow_apps_description')}
        </p>
      </div>
      {allowedApps.length > 0 && (
        <div className='flex flex-wrap items-center ~gap-2/3'>
          {allowedApps.map((appName) => (
            <AllowedItemPill
              key={appName}
              label={appName}
              onRemove={() => handleDeleteApp(appName)}
              removeAriaLabel={t('tabs_revamp.allowed_apps_tab.remove_app', {
                appName
              })}
            />
          ))}
        </div>
      )}
      <div className='flex flex-col ~gap-2/3'>
        <FloatingLabelInput
          type='text'
          placeholder={t('tabs_revamp.allowed_apps_tab.enter_app_name')}
          value={appInput}
          onChange={(event) => setAppInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === KEY_BOARD.ENTER.KEY) {
              handleAddApp();
            }
          }}
          size='small'
          showLabel={true}
          label=''
          {...DEFAULT_HTML_ATTRIBUTES}
        />
        <div className='flex justify-end'>
          <BaseButton
            title={t('tabs_revamp.allowed_apps_tab.add_app')}
            onClick={handleAddApp}
            variant='secondary'
            icon={<Add styles='~w-3/4 ~h-3/4' fill={COLOR.PRIMARY.LIGHT_900} />}
            size='sm'
          />
        </div>
      </div>
    </Card>
  );
};

export default function FocusModeAllowedAppsAndURLsRevamp() {
  return (
    <div className='w-full flex flex-col ~gap-4/6 ~px-1/2 ~py-2/3'>
      <AllowedUrlsCard />
      <AllowedAppsCard />
    </div>
  );
}
